import React, { useEffect, useMemo, useRef, useState } from 'react';
import useTrialHours from './hooks/useTrialHours';
import Dialog from './TivigoDialog';
import Switch from '@mui/material/Switch';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import Groups2RoundedIcon from '@mui/icons-material/Groups2Rounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import TivigoToast from './TivigoToast';
import UserPanelTableLoading from './UserPanelTableLoading';
import {
  hasInvalidCredentialCharacters,
  isCredentialValueAllowed,
  PASSWORD_CHARACTER_ERROR,
  USERNAME_CHARACTER_ERROR,
} from './utils/credentialPolicy';
import './userPanelSubUsers.css';

const DEFAULT_PAGE_SIZE = 5;

const STATUS_FILTERS = [
  { value: 'all', label: 'Tüm Durumlar' },
  { value: 'active', label: 'Aktif Kullanıcılar' },
  { value: 'passive', label: 'Pasif Kullanıcılar' },
];

const TABLE_COLUMNS = [
  { key: 'username', label: 'Kullanıcı' },
  { key: 'password', label: 'Şifre' },
  { key: 'email', label: 'E-posta' },
  { key: 'membership', label: 'Bitiş Tarihi' },
  { key: 'status', label: 'Durum' },
];

const EMPTY_FORM = {
  username: '',
  password: '',
  email: '',
  phone: '',
  isTest: false,
  fullName: '',
  team: '',
};

const normalizeText = (value) => String(value || '')
  .toLocaleLowerCase('tr-TR')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const getInitials = (value) => String(value || '')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part.charAt(0))
  .join('')
  .toLocaleUpperCase('tr-TR') || '—';

const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const toEnabled = (value, fallback = false) => {
  if (value == null || value === '') return fallback;
  if (value === false || value === 0 || value === '0' || value === 'false') return false;
  return true;
};

const parseExpiry = (value) => {
  if (value == null || value === '' || Number(value) === 0) return null;
  const numeric = Number(value);
  const date = Number.isFinite(numeric)
    ? new Date(numeric > 100000000000 ? numeric : numeric * 1000)
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getUserCollection = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.users)) return data.users;
  if (Array.isArray(data?.data?.users)) return data.data.users;
  return [];
};

const normalizeUser = (user, index) => {
  const source = user && typeof user === 'object' ? user : {};
  const username = String(source.username || source.user_name || '');
  const expiryDate = parseExpiry(source.exp_date ?? source.expiry ?? source.expires_at);
  const explicitStatus = normalizeText(source.status);
  const unlimited = Boolean(source.unlimited || source.is_unlimited)
    || explicitStatus === 'sinirsiz'
    || explicitStatus === 'unlimited';
  const enabled = toEnabled(source.enabled, true);
  const adminEnabled = toEnabled(source.admin_enabled, true);
  const explicitlyInactive = ['passive', 'pasif', 'disabled', 'banned', 'yasakli', 'inactive'].includes(explicitStatus);
  const active = enabled
    && !explicitlyInactive
    && Boolean(expiryDate && expiryDate.getTime() > Date.now());

  return {
    ...source,
    id: String(source.id ?? source.user_id ?? source.member_id ?? index),
    username,
    password: String(source.password || ''),
    email: String(source.email || source.mail || ''),
    phone: String(source.phone || source.telephone || ''),
    fullName: String(source.ad || source.fullName || source.name || ''),
    team: String(source.takim || source.team || ''),
    isTest: Boolean(Number(source.is_test ?? source.isTest ?? 0)),
    unlimited,
    enabled,
    adminEnabled,
    active,
    statusLabel: active ? 'Aktif' : 'Pasif',
    expiryDate,
    monthlyEarning: toNumber(
      source.monthly_earning
      ?? source.monthlyEarning
      ?? source.monthly_commission
      ?? source.monthlyCommission,
    ),
    totalEarning: toNumber(
      source.total_earning
      ?? source.totalEarning
      ?? source.total_commission
      ?? source.totalCommission,
    ),
  };
};

const formatCurrency = (value) => (
  new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    maximumFractionDigits: 0,
  }).format(toNumber(value))
);

const formatExpiry = (user) => {
  if (user.unlimited) return 'Sınırsız';
  if (!user.expiryDate) return 'Süre yok';
  return user.expiryDate.toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const maskPassword = (password) => {
  if (!password) return '—';
  return '•'.repeat(Math.min(12, Math.max(8, password.length)));
};

function UserPanelSubUsers({
  data,
  loading = false,
  onCreateUser,
  onManageUser,
  resellerLevel = '',
  discountRate = null,
  resellerInfo,
  stats,
  onLoadTestLimit,
  pageSize = DEFAULT_PAGE_SIZE,
}) {
  const users = useMemo(
    () => getUserCollection(data).map(normalizeUser),
    [data],
  );
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(Math.max(1, toNumber(pageSize, DEFAULT_PAGE_SIZE)));
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [formMessage, setFormMessage] = useState('');
  const [createSaving, setCreateSaving] = useState(false);
  const [testLimitInfo, setTestLimitInfo] = useState(null);
  const [testLimitLoading, setTestLimitLoading] = useState(false);
  const [testLimitError, setTestLimitError] = useState('');
  const trialHours = useTrialHours();
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [revealedPasswords, setRevealedPasswords] = useState(() => new Set());
  const [expandedMobileRows, setExpandedMobileRows] = useState(() => new Set());
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [toast, setToast] = useState(null);
  const statusFilterRef = useRef(null);
  const pageSizeRef = useRef(null);

  useEffect(() => {
    setRowsPerPage(Math.max(1, toNumber(pageSize, DEFAULT_PAGE_SIZE)));
  }, [pageSize]);

  useEffect(() => {
    if (!createOpen) return undefined;
    let active = true;
    setTestLimitLoading(true);
    setTestLimitError('');
    setTestLimitInfo(null);
    if (typeof onLoadTestLimit !== 'function') {
      setTestLimitLoading(false);
      setTestLimitError('Test limit bilgisi alınamadı.');
      return undefined;
    }
    Promise.resolve(onLoadTestLimit())
      .then((payload) => {
        if (!active) return;
        const source = payload?.data || payload || {};
        setTestLimitInfo({
          limit: Number(source.limit) || 0,
          used: Number(source.used) || 0,
          remaining: Number(source.remaining) || 0,
          level: source.level || '',
        });
      })
      .catch((error) => {
        if (active) setTestLimitError(error?.message || 'Test limit bilgisi alınamadı.');
      })
      .finally(() => {
        if (active) setTestLimitLoading(false);
      });
    return () => {
      active = false;
    };
  }, [createOpen, onLoadTestLimit]);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 3600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!statusFilterOpen && !pageSizeOpen) return undefined;

    const handlePointerDown = (event) => {
      if (statusFilterOpen && !statusFilterRef.current?.contains(event.target)) {
        setStatusFilterOpen(false);
      }
      if (pageSizeOpen && !pageSizeRef.current?.contains(event.target)) {
        setPageSizeOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setStatusFilterOpen(false);
      setPageSizeOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [pageSizeOpen, statusFilterOpen]);

  const counts = useMemo(() => {
    const embeddedStats = stats
      || data?.stats
      || data?.data?.stats
      || data?.reseller_stats?.user_stats
      || data?.data?.reseller_stats?.user_stats
      || {};
    const readCount = (value, fallback) => (
      value === null || value === undefined || value === '' ? fallback : toNumber(value, fallback)
    );
    const activeCount = users.filter((user) => user.active).length;
    return {
      total: readCount(embeddedStats.total, users.length),
      active: readCount(embeddedStats.active, activeCount),
      passive: readCount(embeddedStats.passive, users.length - activeCount),
    };
  }, [data, stats, users]);

  const earnings = useMemo(() => {
    const embeddedStats = stats || data?.stats || data?.data?.stats || {};
    const embeddedEarnings = data?.reseller_stats?.earnings
      || data?.data?.reseller_stats?.earnings
      || embeddedStats.earnings
      || data?.earnings
      || data?.data?.earnings
      || null;

    return {
      monthly: embeddedEarnings == null ? null : toNumber(embeddedEarnings.monthly),
      total: embeddedEarnings == null ? null : toNumber(embeddedEarnings.total),
    };
  }, [data, stats]);

  const resolvedLevel = String(
    resellerInfo?.level
    ?? data?.reseller_info?.level
    ?? data?.data?.reseller_info?.level
    ?? resellerLevel,
  );
  const resolvedDiscountSource = resellerInfo?.discount_rate
    ?? data?.reseller_info?.discount_rate
    ?? data?.data?.reseller_info?.discount_rate
    ?? discountRate;
  const resolvedDiscount = resolvedDiscountSource == null || resolvedDiscountSource === ''
    ? null
    : toNumber(resolvedDiscountSource);

  const filteredUsers = useMemo(() => {
    const normalizedQuery = normalizeText(query.trim());

    return users.filter((user) => {
      const filterMatches = activeFilter === 'all'
        || (activeFilter === 'active' && user.active)
        || (activeFilter === 'passive' && !user.active);
      if (!filterMatches) return false;

      if (!normalizedQuery) return true;
      return normalizeText([
        user.username,
        user.email,
        user.fullName,
        user.team,
      ].join(' ')).includes(normalizedQuery);
    });
  }, [activeFilter, query, users]);

  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return filteredUsers;

    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    const getSortValue = (user) => {
      if (sortConfig.key === 'membership') {
        if (user.unlimited) return Number.MAX_SAFE_INTEGER;
        return user.expiryDate?.getTime() || 0;
      }
      if (sortConfig.key === 'status') return user.active ? 1 : 0;
      return user[sortConfig.key] || '';
    };

    return [...filteredUsers].sort((firstUser, secondUser) => {
      const firstValue = getSortValue(firstUser);
      const secondValue = getSortValue(secondUser);

      if (typeof firstValue === 'number' && typeof secondValue === 'number') {
        return (firstValue - secondValue) * direction;
      }

      return String(firstValue).localeCompare(String(secondValue), 'tr', {
        numeric: true,
        sensitivity: 'base',
      }) * direction;
    });
  }, [filteredUsers, sortConfig]);

  const pageCount = Math.max(1, Math.ceil(sortedUsers.length / rowsPerPage));
  const safePage = Math.min(currentPage, pageCount);
  const pageStart = (safePage - 1) * rowsPerPage;
  const visibleUsers = sortedUsers.slice(pageStart, pageStart + rowsPerPage);
  const displayedFrom = filteredUsers.length ? pageStart + 1 : 0;
  const displayedTo = Math.min(pageStart + rowsPerPage, filteredUsers.length);
  const pageOptions = useMemo(
    () => Array.from(new Set([5, 10, 20, rowsPerPage])).sort((a, b) => a - b),
    [rowsPerPage],
  );
  const selectedStatusFilter = STATUS_FILTERS.find((option) => option.value === activeFilter)
    || STATUS_FILTERS[0];
  const hasActiveFilters = Boolean(query.trim()) || activeFilter !== 'all';

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, query, rowsPerPage]);

  useEffect(() => {
    if (currentPage > pageCount) setCurrentPage(pageCount);
  }, [currentPage, pageCount]);

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const openCreateDialog = () => {
    setCreateOpen(true);
    setFormErrors({});
    setFormMessage('');
  };

  const resetCreateForm = () => {
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setFormMessage('');
    setShowCreatePassword(false);
  };

  const closeCreateDialog = () => {
    if (createSaving) return;
    setCreateOpen(false);
    resetCreateForm();
  };

  const updateForm = (field, value) => {
    let nextValue = value;
    if (field === 'phone') {
      nextValue = String(value).replace(/[^0-9+]/g, '');
    }

    setFormData((current) => ({ ...current, [field]: nextValue }));
    setFormErrors((current) => {
      let fieldError = '';
      if (field === 'username' && hasInvalidCredentialCharacters(nextValue)) {
        fieldError = USERNAME_CHARACTER_ERROR;
      }
      if (field === 'password' && hasInvalidCredentialCharacters(nextValue)) {
        fieldError = PASSWORD_CHARACTER_ERROR;
      }
      return { ...current, [field]: fieldError };
    });
    setFormMessage('');
  };

  const validateCreateForm = () => {
    const errors = {};
    const phoneDigits = formData.phone.replace(/\D/g, '');

    if (!formData.username.trim()) errors.username = 'Kullanıcı adı zorunludur.';
    else if (formData.username.length < 4) errors.username = 'En az 4 karakter kullanın.';
    else if (!isCredentialValueAllowed(formData.username)) errors.username = USERNAME_CHARACTER_ERROR;
    else if (users.some((user) => normalizeText(user.username) === normalizeText(formData.username))) {
      errors.username = 'Bu kullanıcı adı zaten listede.';
    }

    if (!formData.password) errors.password = 'Şifre zorunludur.';
    else if (formData.password.length < 6) errors.password = 'En az 6 karakter kullanın.';
    else if (!isCredentialValueAllowed(formData.password)) errors.password = PASSWORD_CHARACTER_ERROR;

    if (!formData.email.trim()) errors.email = 'Email adresi zorunludur.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Geçerli bir email adresi girin.';

    if (!formData.phone.trim()) errors.phone = 'Telefon numarası zorunludur.';
    else if (phoneDigits.length < 10) errors.phone = 'En az 10 haneli telefon girin.';

    if (formData.isTest) {
      if (testLimitLoading) errors.test = 'Günlük test limitiniz kontrol ediliyor.';
      else if (testLimitError || !testLimitInfo) errors.test = testLimitError || 'Test limit bilgisi alınamadı.';
      else if (testLimitInfo.remaining <= 0) errors.test = `Günlük test limitiniz doldu (${testLimitInfo.used}/${testLimitInfo.limit}). Yarın tekrar deneyebilirsiniz.`;
    }

    setFormErrors(errors);
    if (Object.keys(errors).length) {
      setFormMessage('Devam etmek için giriş bilgilerini kontrol edin.');
      return false;
    }
    return true;
  };

  const handleCreateSubmit = async (event) => {
    event.preventDefault();
    if (!validateCreateForm()) return;

    setCreateSaving(true);
    setFormMessage('');

    const payload = {
      username: formData.username.trim(),
      password: formData.password,
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      is_test: formData.isTest ? 1 : 0,
      ad: formData.fullName.trim(),
      takim: formData.team,
      max_connections: 1,
    };

    try {
      const result = typeof onCreateUser === 'function'
        ? await onCreateUser(payload)
        : null;

      if (result === false || result?.success === false) {
        throw new Error(result?.message || result?.data || 'Kullanıcı oluşturulamadı.');
      }

      const resultData = result?.data || {};
      if (formData.isTest && resultData.remaining_tests != null) {
        setTestLimitInfo((current) => ({
          ...(current || {}),
          remaining: Number(resultData.remaining_tests) || 0,
          limit: Number(resultData.daily_test_limit) || current?.limit || 0,
        }));
      }
      setActiveFilter('all');
      setQuery('');
      setCurrentPage(1);
      setCreateOpen(false);
      resetCreateForm();
      setToast({
        type: 'success',
        message: formData.isTest
          ? `${payload.username} için ${trialHours} saatlik test hesabı açıldı.`
          : payload.username + ' oluşturuldu. Şimdi süre ekleyebilirsiniz.',
      });
    } catch (error) {
      setFormMessage(error?.message || 'Bağlantı sırasında bir hata oluştu.');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleManageUser = (user) => {
    setSelectedUserId(user.id);

    if (typeof onManageUser === 'function') {
      try {
        Promise.resolve(onManageUser(user)).catch((error) => {
          setToast({ type: 'error', message: error?.message || 'Kullanıcı yönetimi açılamadı.' });
        });
      } catch (error) {
        setToast({ type: 'error', message: error?.message || 'Kullanıcı yönetimi açılamadı.' });
      }
      return;
    }

    setToast({
      type: 'error',
      message: 'Kullanıcı yönetimi bağlantısı kullanılamıyor.',
    });
  };

  const handleRowKeyDown = (event, user) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleManageUser(user);
  };

  const togglePassword = (userId) => {
    setRevealedPasswords((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleMobileDetails = (userId) => {
    setExpandedMobileRows((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const clearFilters = () => {
    setQuery('');
    setActiveFilter('all');
    setCurrentPage(1);
    setStatusFilterOpen(false);
  };

  return (
    <div className="tvup-subusers-page">
      <section className="tvup-subusers-hero" aria-labelledby="tvup-subusers-title">
        <div className="tvup-subusers-hero-copy">
          <h1 id="tvup-subusers-title">Alt kullanıcılarını <span>tek merkezden yönet.</span></h1>
          <p className="tvup-subusers-hero-description">
            Kullanıcılarını takip et, yeni hesap oluştur ve üyelik işlemlerini aynı ekrandan yönet.
          </p>

          <button type="button" className="tvup-subusers-primary-action" onClick={openCreateDialog}>
            <PersonAddAlt1RoundedIcon />
            <span>Yeni Kullanıcı Oluştur</span>
          </button>
        </div>

        <div className="tvup-subusers-hero-visual" aria-hidden="true">
          <div className="tvup-subusers-visual-orbit is-wide" />
          <div className="tvup-subusers-visual-orbit is-round" />

          <div className="tvup-subusers-avatar-stack">
            {users.slice(0, 3).map((portfolioUser) => (
              <span key={portfolioUser.id}>{getInitials(portfolioUser.fullName || portfolioUser.username)}</span>
            ))}
            {loading ? <span>…</span> : counts.total > 3 ? <span>+{counts.total - 3}</span> : null}
          </div>

          <article className="tvup-subusers-active-card">
            <span className="tvup-subusers-active-icon"><Groups2RoundedIcon /></span>
            <span className="tvup-subusers-active-copy">
              <small>KULLANICI PORTFÖYÜ</small>
              <b>{loading ? 'Hesaplanıyor' : counts.active + ' aktif hesap'}</b>
              <em>{loading ? 'Kullanıcılar yükleniyor' : counts.total + ' toplam kullanıcı'}</em>
            </span>
            <i />
          </article>

          <article className="tvup-subusers-advantage-card">
            <span><LocalOfferRoundedIcon /></span>
            <div>
              <small>BAYİ AVANTAJI</small>
              <b>{resolvedDiscount == null ? '—' : `%${resolvedDiscount}`}</b>
              <em>{resolvedLevel ? `${resolvedLevel} seviye` : 'Seviye bilgisi yok'}</em>
            </div>
          </article>

          <article className="tvup-subusers-earning-card">
            <TrendingUpRoundedIcon />
            <span>
              <small>BU AY</small>
              <b>{loading || earnings.monthly == null ? '—' : formatCurrency(earnings.monthly)}</b>
            </span>
          </article>
        </div>
      </section>

      <section className="tvup-subusers-list-panel" aria-labelledby="tvup-subusers-list-title">
        <header className="tvup-subusers-list-toolbar">
          <h2 id="tvup-subusers-list-title">Alt Kullanıcılarım</h2>

          <div className="tvup-subusers-list-tools">
            <label className="tvup-subusers-search-field">
              <SearchRoundedIcon aria-hidden="true" />
              <input
                type="text"
                value={query}
                aria-label="Alt kullanıcılar içinde ara"
                onChange={(event) => {
                  setQuery(event.target.value);
                  setCurrentPage(1);
                }}
              />
            </label>

            <div className="tvup-subusers-status-filter" ref={statusFilterRef}>
              <button
                type="button"
                className="tvup-subusers-status-trigger"
                aria-haspopup="listbox"
                aria-expanded={statusFilterOpen}
                onClick={() => {
                  setStatusFilterOpen((current) => !current);
                  setPageSizeOpen(false);
                }}
              >
                <FilterListRoundedIcon />
                <span>{selectedStatusFilter.label}</span>
                <KeyboardArrowDownRoundedIcon />
              </button>

              {statusFilterOpen && (
                <div className="tvup-subusers-status-menu" role="listbox" aria-label="Kullanıcı durumu">
                  {STATUS_FILTERS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="option"
                      aria-selected={activeFilter === option.value}
                      className={activeFilter === option.value ? 'is-selected' : undefined}
                      onClick={() => {
                        setActiveFilter(option.value);
                        setCurrentPage(1);
                        setStatusFilterOpen(false);
                      }}
                    >
                      <span>{option.label}</span>
                      <small>
                        {option.value === 'all'
                          ? counts.total
                          : option.value === 'active'
                            ? counts.active
                            : counts.passive}
                      </small>
                    </button>
                  ))}
                </div>
              )}
            </div>

          </div>
        </header>

        <div className="tvup-subusers-table-wrap">
          <div className="tvup-subusers-data-table" role="table" aria-label="Alt kullanıcılar">
            <div className="tvup-subusers-table-head" role="row">
              {TABLE_COLUMNS.map((column) => {
                const isActive = sortConfig.key === column.key;
                return (
                  <span
                    key={column.key}
                    role="columnheader"
                    aria-sort={isActive ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <button
                      type="button"
                      className={'tvup-subusers-sort-button' + (isActive ? ' is-active' : '') + (isActive && sortConfig.direction === 'asc' ? ' is-ascending' : '')}
                      onClick={() => handleSort(column.key)}
                    >
                      <span>{column.label}</span>
                      {isActive ? <KeyboardArrowDownRoundedIcon /> : <UnfoldMoreRoundedIcon />}
                    </button>
                  </span>
                );
              })}
              <span role="columnheader">İşlem</span>
            </div>

            {loading ? (
              <UserPanelTableLoading
                rows={1}
                columns={6}
                label="Kullanıcılar yükleniyor"
                className="is-compact"
              />
            ) : visibleUsers.length > 0 ? (
              <div className="tvup-subusers-table-body" role="rowgroup">
                {visibleUsers.map((user) => {
                  const passwordVisible = revealedPasswords.has(user.id);
                  const mobileDetailsExpanded = expandedMobileRows.has(user.id);
                  const selected = selectedUserId === user.id;

                  return (
                    <div
                      key={user.id}
                      className={'tvup-subusers-row' + (selected ? ' is-selected' : '') + (mobileDetailsExpanded ? ' is-mobile-expanded' : '')}
                      role="row"
                      tabIndex={0}
                      aria-selected={selected}
                      onClick={() => handleManageUser(user)}
                      onKeyDown={(event) => handleRowKeyDown(event, user)}
                    >
                      <div className="tvup-subusers-user-cell" role="cell" data-label="Kullanıcı">
                        <strong>{user.username}</strong>
                      </div>

                      <div className="tvup-subusers-password-cell" role="cell" data-label="Şifre">
                        <code>{passwordVisible ? user.password || '—' : maskPassword(user.password)}</code>
                        {user.password && (
                          <button
                            type="button"
                            aria-label={passwordVisible ? 'Şifreyi gizle' : 'Şifreyi göster'}
                            aria-pressed={passwordVisible}
                            onClick={(event) => {
                              event.stopPropagation();
                              togglePassword(user.id);
                            }}
                          >
                            {passwordVisible ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                          </button>
                        )}
                      </div>

                      <div className="tvup-subusers-email-cell" role="cell" data-label="E-posta">
                        <span title={user.email || 'E-posta bilgisi yok'}>{user.email || '—'}</span>
                      </div>

                      <div className="tvup-subusers-membership-cell" role="cell" data-label="Bitiş Tarihi">
                        <strong>{formatExpiry(user)}</strong>
                      </div>

                      <div className="tvup-subusers-status-cell" role="cell" data-label="Durum">
                        <span className={user.active ? 'is-active' : 'is-passive'}>
                          <i />
                          {user.statusLabel}
                        </span>
                      </div>

                      <div className="tvup-subusers-action-cell" role="cell" data-label="İşlem">
                        <button
                          type="button"
                          className="tvup-subusers-manage-button"
                          aria-label={user.username + ' kullanıcısını yönet'}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleManageUser(user);
                          }}
                        >
                          <span>Yönet</span>
                          <ChevronRightRoundedIcon />
                        </button>
                      </div>

                      <button
                        type="button"
                        className="tvup-subusers-mobile-details"
                        aria-expanded={mobileDetailsExpanded}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleMobileDetails(user.id);
                        }}
                      >
                        <span>{mobileDetailsExpanded ? 'Detayları Gizle' : 'Detaylar'}</span>
                        <KeyboardArrowDownRoundedIcon />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="tvup-subusers-empty" role="status">
                <span>{hasActiveFilters ? <SearchRoundedIcon /> : <Groups2RoundedIcon />}</span>
                <strong>{hasActiveFilters ? 'Sonuç bulunamadı' : 'Henüz alt kullanıcınız yok'}</strong>
                <p>
                  {hasActiveFilters
                    ? 'Arama metnini veya durum filtresini değiştirin.'
                    : 'İlk alt kullanıcı hesabınızı oluşturarak portföyünüzü başlatın.'}
                </p>
                <button
                  type="button"
                  onClick={hasActiveFilters ? clearFilters : openCreateDialog}
                >
                  {hasActiveFilters ? 'Filtreleri Temizle' : 'Yeni Kullanıcı Oluştur'}
                </button>
              </div>
            )}
          </div>
        </div>

        {!loading && filteredUsers.length > 0 && (
          <footer className="tvup-subusers-pagination" aria-label="Alt kullanıcı sayfalama">
            <div className="tvup-subusers-page-size">
              <span>Sayfa başına</span>
              <div className="tvup-subusers-page-size-dropdown" ref={pageSizeRef}>
                <button
                  type="button"
                  className="tvup-subusers-page-size-trigger"
                  aria-haspopup="listbox"
                  aria-expanded={pageSizeOpen}
                  onClick={() => {
                    setPageSizeOpen((current) => !current);
                    setStatusFilterOpen(false);
                  }}
                >
                  <span>{rowsPerPage}</span>
                  <KeyboardArrowDownRoundedIcon />
                </button>

                {pageSizeOpen && (
                  <div className="tvup-subusers-page-size-menu" role="listbox" aria-label="Sayfa başına kullanıcı sayısı">
                    {pageOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        role="option"
                        aria-selected={rowsPerPage === option}
                        className={rowsPerPage === option ? 'is-selected' : undefined}
                        onClick={() => {
                          setRowsPerPage(option);
                          setCurrentPage(1);
                          setPageSizeOpen(false);
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <span className="tvup-subusers-page-range">
              {displayedFrom}–{displayedTo} / {filteredUsers.length} kayıt
            </span>

            <div className="tvup-subusers-pagination-controls">
              <button
                type="button"
                aria-label="Önceki sayfa"
                disabled={safePage <= 1}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              >
                <ChevronLeftRoundedIcon />
              </button>
              <strong>{safePage} / {pageCount}</strong>
              <button
                type="button"
                aria-label="Sonraki sayfa"
                disabled={safePage >= pageCount}
                onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
              >
                <ChevronRightRoundedIcon />
              </button>
            </div>
          </footer>
        )}
      </section>

      <Dialog
        open={createOpen}
        maxWidth={false}
        keepMounted
        transitionDuration={0}
        disableEscapeKeyDown
        className="tvup-subusers-dialog"
        aria-labelledby="tvup-subusers-create-title"
        slotProps={{
          backdrop: { className: 'tvup-subusers-backdrop' },
          paper: { className: 'tvup-subusers-modal' },
        }}
      >
        <form className="tvup-subusers-create-form" onSubmit={handleCreateSubmit} noValidate>
          <header className="tvup-subusers-modal-header">
            <span className="tvup-subusers-modal-title-icon"><PersonAddAlt1RoundedIcon /></span>
            <div>
              <h2 id="tvup-subusers-create-title">Yeni Kullanıcı Aç</h2>
              <p>Alt kullanıcı hesabını birkaç adımda hazırla.</p>
            </div>
            <button type="button" aria-label="Yeni kullanıcı penceresini kapat" onClick={closeCreateDialog}>
              <CloseRoundedIcon />
            </button>
          </header>

          {formMessage && (
            <div className="tvup-subusers-form-message" role="alert">{formMessage}</div>
          )}

          <div className="tvup-subusers-modal-body">
            <section className="tvup-subusers-form-panel">
              <div className="tvup-subusers-form-grid">
                <label className={formErrors.username ? 'tvup-subusers-field-error' : undefined}>
                  <span>Kullanıcı Adı <i>*</i></span>
                  <div className="tvup-subusers-input-wrap">
                    <input
                      name="username"
                      value={formData.username}
                      autoComplete="off"
                      onChange={(event) => updateForm('username', event.target.value)}
                    />
                  </div>
                  {formErrors.username && <small>{formErrors.username}</small>}
                </label>

                <label className={formErrors.password ? 'tvup-subusers-field-error' : undefined}>
                  <span>Şifre <i>*</i></span>
                  <div className="tvup-subusers-input-wrap">
                    <input
                      name="password"
                      type={showCreatePassword ? 'text' : 'password'}
                      value={formData.password}
                      autoComplete="new-password"
                      onChange={(event) => updateForm('password', event.target.value)}
                    />
                    <button
                      type="button"
                      aria-label={showCreatePassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                      onClick={() => setShowCreatePassword((visible) => !visible)}
                    >
                      {showCreatePassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                    </button>
                  </div>
                  {formErrors.password && <small>{formErrors.password}</small>}
                </label>

                <label className={formErrors.email ? 'tvup-subusers-field-error' : undefined}>
                  <span>E-posta <i>*</i></span>
                  <div className="tvup-subusers-input-wrap">
                    <input
                      name="email"
                      type="email"
                      value={formData.email}
                      autoComplete="off"
                      onChange={(event) => updateForm('email', event.target.value)}
                    />
                  </div>
                  {formErrors.email && <small>{formErrors.email}</small>}
                </label>

                <label className={formErrors.phone ? 'tvup-subusers-field-error' : undefined}>
                  <span>Telefon <i>*</i></span>
                  <div className="tvup-subusers-input-wrap">
                    <input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      autoComplete="off"
                      onChange={(event) => updateForm('phone', event.target.value)}
                    />
                  </div>
                  {formErrors.phone && <small>{formErrors.phone}</small>}
                </label>
              </div>

              <div className={'tvup-subusers-test-option ' + (formData.isTest ? 'tvup-subusers-test-option-active' : '')}>
                <span className="tvup-subusers-test-option-icon"><ScheduleRoundedIcon /></span>
                <span className="tvup-subusers-test-option-copy">
                  <b>{trialHours} Saatlik Test Hesabı</b>
                  <small>{testLimitLoading
                    ? 'Günlük test limiti kontrol ediliyor.'
                    : testLimitError
                      ? testLimitError
                      : testLimitInfo
                        ? `Günlük kalan test: ${testLimitInfo.remaining}/${testLimitInfo.limit}`
                        : `Aktif edildiğinde kullanıcıya anında ${trialHours} saatlik erişim tanımlanır.`}</small>
                </span>
                <Switch
                  checked={formData.isTest}
                  onChange={(event) => updateForm('isTest', event.target.checked)}
                  inputProps={{ 'aria-label': `${trialHours} saatlik test hesabı` }}
                  className="tvup-subusers-test-switch"
                />
              </div>
              {formErrors.test ? <small className="tvup-subusers-form-error">{formErrors.test}</small> : null}

              <div className="tvup-subusers-info-note">
                <span>Paket tanımlama, kullanıcı hesabı oluşturulduktan sonra yapılır.</span>
              </div>
            </section>
          </div>

          <footer className="tvup-subusers-modal-footer">
            <button type="submit" disabled={createSaving}>
              {createSaving ? <i className="tvup-subusers-button-spinner" /> : <PersonAddAlt1RoundedIcon />}
              {createSaving
                ? 'Oluşturuluyor...'
                : formData.isTest
                  ? `${trialHours} Saatlik Test Hesabı Oluştur`
                  : 'Kullanıcı Oluştur'}
            </button>
          </footer>
        </form>
      </Dialog>

      <TivigoToast notice={toast} />
    </div>
  );
}

export default UserPanelSubUsers;
